import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { matchNav } from '@/layouts/navigation'

export interface Crumb {
  label: string
  to?: string
}

/**
 * The breadcrumb for the current route, derived from the navigation tree.
 *
 * <p>Derived rather than hand-written on each page, because the two drifted: a page whose
 * breadcrumb says "Attendance › Devices" while the sidebar has moved Devices somewhere else
 * is telling the reader something untrue about the shape of the product, and nothing fails
 * when it happens.
 *
 * <p>A page may append its own trailing crumbs — a record id, a tab — which the tree cannot
 * know about.
 */
export function useBreadcrumb(...trailing: (Crumb | string)[]): Crumb[] {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const match = matchNav(pathname)

  const crumbs: Crumb[] = []
  if (match) {
    crumbs.push({
      label: t(`nav.${match.module.key}`),
      // A module that only holds children has nowhere of its own to link to; the crumb is
      // then a label rather than a dead link to a page that does not exist.
      to: match.module.to,
    })
    if (match.child) {
      crumbs.push({ label: t(`navChild.${match.child.key}`), to: match.child.to })
    }
  }

  for (const crumb of trailing) {
    crumbs.push(typeof crumb === 'string' ? { label: crumb } : crumb)
  }

  // The last crumb is where you already are, so it is never a link.
  return crumbs.map((crumb, index) => (index === crumbs.length - 1 ? { label: crumb.label } : crumb))
}

/** The page title for the top bar — the deepest thing the tree knows about this route. */
export function usePageTitle(): string {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const match = matchNav(pathname)

  if (!match) {
    return t('app.name')
  }
  return match.child ? t(`navChild.${match.child.key}`) : t(`nav.${match.module.key}`)
}
